import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { CameraScanner } from "@/components/CameraScanner";
import { QuickAddModal } from "@/components/QuickAddModal";
import { ScanLine, X, Camera, Image as ImageIcon, Plus, ShieldOff, Check, Loader2, ArrowLeft } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const MAX_PHOTOS = 4;

// Downscale a picked/captured image to keep upload + AI-clean fast.
const resizeImage = (file, max = 1200) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      const ratio = Math.min(max / img.width, max / img.height, 1);
      const w = Math.round(img.width * ratio), h = Math.round(img.height * ratio);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject; img.src = reader.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

export default function ScanPhotos() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");

  const refreshMe = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setMe(r.data); } catch { setMe(null); }
    setChecking(false);
  }, []);
  useEffect(() => { refreshMe(); }, [refreshMe]);

  const doLogin = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/login", { email, password: pw }); await refreshMe(); toast.success("Signed in"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
  };

  if (checking) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const canUse = me && (me.permissions || []).includes("MANAGE_PRODUCTS");
  if (!canUse) {
    return (
      <div className="min-h-screen grid place-items-center bg-brand-navy p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm" data-testid="scanphotos-login">
          <div className="text-brand-red font-black uppercase tracking-widest text-xs text-center">DealsWagon</div>
          <h1 className="font-heading text-2xl text-brand-navy text-center mt-1">Scan to add photos</h1>
          {me ? (
            <div className="mt-4 text-center text-slate-600">
              <ShieldOff className="h-8 w-8 mx-auto text-brand-red" />
              <p className="mt-2 text-sm">Signed in as {me.email} — no product-management access.</p>
            </div>
          ) : (
            <form onSubmit={doLogin} className="mt-5 space-y-3">
              <input data-testid="scanphotos-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-4" />
              <input data-testid="scanphotos-password" type="password" required placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-4" />
              <button data-testid="scanphotos-login-submit" className="w-full h-12 rounded-full bg-brand-navy text-white font-bold">Sign in</button>
            </form>
          )}
        </div>
      </div>
    );
  }
  return <ScanPhotosScreen />;
}

const ScanPhotosScreen = () => {
  const [camOpen, setCamOpen] = useState(false);
  const [pickList, setPickList] = useState(null);
  const [notFound, setNotFound] = useState("");
  const [quickAdd, setQuickAdd] = useState(null);
  const [product, setProduct] = useState(null);
  const [photos, setPhotos] = useState([]); // {original, cleaned, use, status}
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const lookup = async (code) => {
    setNotFound("");
    try {
      const r = await api.get("/pos/photo/lookup", { params: { q: code } });
      const items = r.data.items || [];
      if (items.length === 1) selectProduct(items[0]);
      else if (items.length > 1) { setPickList(items); setCamOpen(false); }
      else { setQuickAdd(code); setCamOpen(false); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); }
  };

  const selectProduct = (p) => {
    setProduct(p); setPickList(null); setCamOpen(false); setNotFound("");
    setPhotos([]); setQty(String(p.quantity_available ?? "")); setPrice(String(p.price ?? ""));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (photos.length >= MAX_PHOTOS) { toast.error(`Up to ${MAX_PHOTOS} photos`); return; }
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    let dataUrl;
    try { dataUrl = await resizeImage(f); } catch { toast.error("Could not read that image"); return; }
    const idx = photos.length;
    setPhotos((prev) => [...prev, { original: dataUrl, cleaned: null, use: "cleaned", status: "cleaning" }]);
    try {
      const r = await api.post("/pos/photo/clean", { image_base64: dataUrl });
      setPhotos((prev) => prev.map((ph, i) => i === idx ? { ...ph, cleaned: r.data.cleaned_base64, status: "done" } : ph));
    } catch {
      toast.message("Auto-cleanup unavailable — keeping the original.");
      setPhotos((prev) => prev.map((ph, i) => i === idx ? { ...ph, use: "original", status: "failed" } : ph));
    }
  };

  const setUse = (i, use) => setPhotos((prev) => prev.map((ph, k) => k === i ? { ...ph, use } : ph));
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, k) => k !== i));

  const save = async () => {
    if (!photos.length) { toast.error("Add at least one photo"); return; }
    if (photos.some((p) => p.status === "cleaning")) { toast.error("Still cleaning a photo — hold on"); return; }
    const images = photos.map((p) => (p.use === "cleaned" && p.cleaned ? p.cleaned : p.original));
    setSaving(true);
    try {
      await api.post("/pos/photo/save", {
        product_id: product.product_id, images,
        price: price === "" ? null : parseFloat(price),
        quantity: qty === "" ? null : parseInt(qty, 10),
      });
      toast.success("Photos saved to product");
      setProduct(null); setPhotos([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="scanphotos-screen">
      <header className="bg-brand-navy text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2"><ScanLine className="h-6 w-6 text-brand-yellow" /><span className="font-heading text-lg">Scan &amp; add photos</span></div>
        <a href="/admin" className="text-sm opacity-80 hover:opacity-100">Admin</a>
      </header>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {!product && (
          <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
            <p className="text-slate-500 text-sm">Scan a product barcode to add clean, consistent photos.</p>
            <button onClick={() => { setNotFound(""); setCamOpen(true); }} data-testid="scanphotos-scan-btn" className="mt-4 w-full h-14 rounded-xl bg-brand-green text-white font-heading text-lg inline-flex items-center justify-center gap-2"><Camera className="h-5 w-5" /> Scan</button>
            {notFound && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm" data-testid="scanphotos-notfound">
                <div className="font-bold text-amber-800">"{notFound}" isn't in your catalog yet.</div>
                <a href="/admin" className="inline-block mt-2 text-brand-navy font-bold underline">Add it in the product form</a>
              </div>
            )}
          </div>
        )}

        {product && (
          <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="scanphotos-editor">
            <button onClick={() => setProduct(null)} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Scan another</button>
            <div>
              <div className="font-heading text-xl text-brand-navy">{product.product_name}</div>
              <div className="text-xs text-slate-500">{[product.sku, product.meta].filter(Boolean).join(" · ")}</div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {photos.map((ph, i) => {
                const showCleaned = ph.use === "cleaned" && ph.cleaned;
                const src = showCleaned ? ph.cleaned : ph.original;
                return (
                  <div key={i} className="relative border rounded-xl overflow-hidden bg-slate-50" data-testid={`scanphotos-photo-${i}`}>
                    <img src={src} alt="" className="w-full aspect-square object-contain bg-white" />
                    {i === 0 && <span className="absolute top-1 left-1 text-[10px] font-black bg-brand-green text-white px-1.5 py-0.5 rounded-full">MAIN</span>}
                    <button onClick={() => removePhoto(i)} data-testid={`scanphotos-remove-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="h-3.5 w-3.5" /></button>
                    <div className="p-1.5 text-[11px]">
                      {ph.status === "cleaning" ? (
                        <div className="flex items-center gap-1 text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Cleaning…</div>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => setUse(i, "cleaned")} disabled={!ph.cleaned} data-testid={`scanphotos-use-cleaned-${i}`} className={`flex-1 rounded px-1 py-0.5 font-bold ${ph.use === "cleaned" && ph.cleaned ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"} disabled:opacity-40`}>Cleaned</button>
                          <button onClick={() => setUse(i, "original")} data-testid={`scanphotos-use-original-${i}`} className={`flex-1 rounded px-1 py-0.5 font-bold ${ph.use === "original" ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"}`}>Original</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {photos.length < MAX_PHOTOS && (
                <button onClick={() => fileRef.current?.click()} data-testid="scanphotos-add-photo" className="border-2 border-dashed border-slate-300 rounded-xl aspect-square grid place-items-center text-slate-400 hover:border-brand-green hover:text-brand-green">
                  <div className="text-center"><Plus className="h-7 w-7 mx-auto" /><span className="text-xs font-bold">Add photo</span></div>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} data-testid="scanphotos-file-input" className="hidden" />

            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-bold text-slate-600">Quantity</span>
                <input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="scanphotos-qty" className="mt-1 w-full h-11 border rounded-lg px-3 text-right" /></label>
              <label className="block"><span className="text-xs font-bold text-slate-600">Price</span>
                <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="scanphotos-price" className="mt-1 w-full h-11 border rounded-lg px-3 text-right" /></label>
            </div>

            <button onClick={save} disabled={saving || !photos.length} data-testid="scanphotos-save" className="w-full h-14 rounded-xl bg-brand-green text-white font-heading text-lg disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {saving ? <><Loader2 className="h-5 w-5 animate-spin" /> Saving…</> : <><Check className="h-5 w-5" /> Save photos</>}
            </button>
          </div>
        )}
      </div>

      {camOpen && <CameraScanner title="Scan a product" onDetect={lookup} onClose={() => setCamOpen(false)} />}

      {quickAdd !== null && <QuickAddModal code={quickAdd} onClose={() => setQuickAdd(null)}
        onCreated={(item) => {
          selectProduct({
            product_id: item.product_id, product_name: item.product_name, sku: item.sku,
            price: item.price, quantity_available: item.quantity_available ?? item.available ?? 0,
            featured_image: item.featured_image, images: item.images || [], meta: item.meta || "",
          });
          setQuickAdd(null);
        }} />}

      {pickList && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setPickList(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="scanphotos-picker">
            <div className="flex items-center justify-between px-4 py-3 border-b"><span className="font-heading text-lg text-brand-navy">Choose the item</span><button onClick={() => setPickList(null)}><X className="h-5 w-5 text-slate-500" /></button></div>
            <div className="divide-y">
              {pickList.map((p) => (
                <button key={p.product_id} onClick={() => selectProduct(p)} data-testid={`scanphotos-pick-${p.product_id}`} className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-3">
                  {p.featured_image ? <img src={mediaUrl(p.featured_image)} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-slate-100 grid place-items-center text-slate-400"><ImageIcon className="h-5 w-5" /></div>}
                  <div className="min-w-0"><div className="font-bold text-sm text-brand-navy truncate">{p.product_name}</div><div className="text-xs text-slate-500">{p.sku} · {money(p.price)} · {p.quantity_available} in stock</div></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
